import { useTheme } from '@mui/material';
import { AuthenticatedLink } from '../../components/atoms/AuthenticatedLink';
import { getConfig } from '../../providers/ConfigProvider';
import { DocumentDto } from './models/ClientEntityDto';
import { useStyles } from './style';
import {
  Table,
  Icons,
  GridRowsProp,
  GridRowParams,
  TypeSafeColDef,
} from '@forgeglobal/forge-component-library';

interface StatementsTableProps {
  documents: DocumentDto[];
}

/**
 * Displays a table of statement documents with download functionality.
 * Renders period (year), file name, created date, and a download action column.
 */
export const StatementsTable = ({ documents }: StatementsTableProps) => {
  const { classes } = useStyles();
  const theme = useTheme();

  type DocumentRow = DocumentDto & { id: string };

  const rows: GridRowsProp<DocumentRow> = documents.map((d) => ({
    ...d,
    id: d.DocumentId ?? '',
  }));

  const columns: TypeSafeColDef<DocumentRow>[] = [
    { field: 'Period', headerName: 'Period' },
    {
      field: 'FileName',
      headerName: 'File name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'CreatedDate',
      headerName: 'Date',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'actions',
      headerName: 'Download',
      type: 'actions',
      getActions: (rowParams: GridRowParams<DocumentRow>) => {
        const url = `${
          getConfig().API_BASE_URL
        }/documents/statements/download/${rowParams.row.DocumentId}`;
        return [
          <AuthenticatedLink
            title="Download..."
            url={url}
            fileName={rowParams.row?.FileName}
            className={classes.downloadLink}
            defaultFileName={'statement.pdf'}
            openInNewTab={false}>
            <Icons.Download className={classes.downloadIcon} />
          </AuthenticatedLink>,
        ];
      },
    },
  ];

  return (
    <div data-test-id="statements-table">
      <Table
        sx={{ padding: 0, borderRight: 'none', borderBottom: 'none' }}
        dataGridProps={{
          sx: {
            paddingTop: 0,
            backgroundColor: theme.palette.background.paper,
          },
          columns: columns,
          rows: rows,
          hideFooter: true,
        }}
      />
    </div>
  );
};

